import firebase from 'firebase/compat/app'
import 'firebase/compat/messaging'
import { database } from './firebase'
import config from './config'

const SW_PATH = '/firebase-messaging-sw.js'

// CRA copies public/ verbatim, so the service worker cannot read process.env.
// The config it needs travels on the registration URL instead.
const swUrl = () => {
    const { apiKey, authDomain, projectId, storageBucket, messagingSenderId, appId } =
        config.firebase
    const params = new URLSearchParams({
        apiKey: apiKey || '',
        authDomain: authDomain || '',
        projectId: projectId || '',
        storageBucket: storageBucket || '',
        messagingSenderId: messagingSenderId || '',
        appId: appId || '',
    })
    return `${SW_PATH}?${params.toString()}`
}

export function isPushSupported() {
    return (
        typeof window !== 'undefined' &&
        !!window.Notification &&
        typeof navigator !== 'undefined' &&
        !!navigator.serviceWorker &&
        typeof window.PushManager !== 'undefined'
    )
}

export function getPermission() {
    if (!isPushSupported()) return 'unsupported'
    return window.Notification.permission
}

async function registerAndStore(uid) {
    const registration = await navigator.serviceWorker.register(swUrl())
    const token = await firebase.messaging().getToken({
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
    })
    if (!token) return { ok: false, reason: 'no-token' }

    // update() on the user node — never set(), which would wipe the record.
    await database.ref(`users/${uid}`).update({ webNotificationToken: token })
    return { ok: true, token }
}

// Notifications are a nice-to-have: every failure path resolves with a reason so
// a browser quirk can never break the screen that called this.
export async function requestPermissionAndRegister(uid) {
    if (!isPushSupported()) return { ok: false, reason: 'unsupported' }
    if (!uid) return { ok: false, reason: 'no-uid' }

    try {
        const permission =
            window.Notification.permission === 'default'
                ? await window.Notification.requestPermission()
                : window.Notification.permission

        if (permission !== 'granted') return { ok: false, reason: permission }
        return await registerAndStore(uid)
    } catch (error) {
        console.log('web push registration failed:', error?.message)
        return { ok: false, reason: 'error' }
    }
}

const CLEAR_TIMEOUT_MS = 2000

// Must run on every sign-out, BEFORE auth.signOut(): a browser's FCM token
// belongs to the BROWSER, not the account. Left behind, the next patient to sign
// in on this device registers the same token and inherits the previous patient's
// doctor messages. It has to precede signOut because the RTDB rules only permit
// writing users/{uid} while that uid is still authenticated.
export async function clearWebToken(uid) {
    if (!uid) return { ok: false, reason: 'no-uid' }

    try {
        // RTDB update() settles only on server ack — offline it never settles at
        // all, which would hang the sign-out awaiting this. Bound the wait so a
        // dropped connection costs a stale token, not a stuck logout.
        const cleared = await Promise.race([
            database
                .ref(`users/${uid}`)
                .update({ webNotificationToken: null })
                .then(() => true),
            new Promise((resolve) => setTimeout(() => resolve(false), CLEAR_TIMEOUT_MS)),
        ])
        return cleared ? { ok: true } : { ok: false, reason: 'timeout' }
    } catch (error) {
        console.log('web push token clear failed:', error?.message)
        return { ok: false, reason: 'error' }
    }
}

export async function syncTokenIfGranted(uid) {
    if (!isPushSupported()) return { ok: false, reason: 'unsupported' }
    if (!uid) return { ok: false, reason: 'no-uid' }
    if (window.Notification.permission !== 'granted') return { ok: false, reason: 'not-granted' }

    try {
        return await registerAndStore(uid)
    } catch (error) {
        console.log('web push token sync failed:', error?.message)
        return { ok: false, reason: 'error' }
    }
}
